import {
    BlankModal,
    Button,
    ButtonGroup,
    ModalBody,
    ModalFooter,
    ModalHeader,
    useToast,
} from '@cad-arc';
import {
    ApiResultSaveTowVehicleHoldResponse,
    BooleanValueOption,
    EventPanelResource,
    SaveTowVehicleHoldRequest,
} from '@mark43/cad-api';
import React from 'react';
import { BaseSelect } from '../../../../forms/inputs/base-select';
import { useApiClient } from '@m43/core';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { errorOccurredWhile } from '../../../../common/helpers/error-occurred-while';

interface TowVehicleHoldSelectProps {
    value: boolean | undefined;
    options: BooleanValueOption[];
    towVehicleId?: number | null;
    onConfirmChange: (nextValue: BooleanValueOption) => void;
}

export function TowVehicleHoldSelect({
    value,
    options,
    towVehicleId,
    onConfirmChange,
}: TowVehicleHoldSelectProps) {
    const apiClient = useApiClient();
    const toast = useToast();
    const [pendingOption, setPendingOption] = React.useState<BooleanValueOption | null>(null);
    const saveTowVehicleHoldMutation = useMutation<
        ApiResultSaveTowVehicleHoldResponse,
        Error,
        SaveTowVehicleHoldRequest
    >({
        mutationFn: (data) =>
            apiClient
                .request<EventPanelResource.SaveTowVehicleHold>({
                    method: 'POST',
                    url: 'event_panel/tow_vehicle_hold/',
                    data,
                })
                .then((resp) => resp.data),
    });

    return (
        <>
            <BaseSelect
                placeholder="Select status"
                value={value}
                options={options}
                isClearable={false}
                onChange={(event) => {
                    const nextValue = String(event.target.value).toLowerCase() === 'true';
                    if (value === nextValue) {
                        return;
                    }
                    const nextOption = options.find((option) => option.value === nextValue);
                    if (nextOption) {
                        setPendingOption(nextOption);
                    }
                }}
                onClick={(event) => {
                    event.stopPropagation();
                }}
            />
            {pendingOption && (
                <TowVehicleHoldConfirmModal
                    towVehicleId={towVehicleId}
                    nextOption={pendingOption}
                    options={options}
                    onConfirmChange={onConfirmChange}
                    onClose={() => {
                        if (!saveTowVehicleHoldMutation.isLoading) {
                            setPendingOption(null);
                        }
                    }}
                    mutation={saveTowVehicleHoldMutation}
                    toast={toast}
                />
            )}
        </>
    );
}

function TowVehicleHoldConfirmModal({
    towVehicleId,
    nextOption,
    options,
    onConfirmChange,
    onClose,
    mutation,
    toast,
}: {
    towVehicleId?: number | null;
    nextOption: BooleanValueOption;
    options: BooleanValueOption[];
    onConfirmChange: (nextValue: BooleanValueOption) => void;
    onClose: () => void;
    mutation: UseMutationResult<
        ApiResultSaveTowVehicleHoldResponse,
        Error,
        SaveTowVehicleHoldRequest
    >;
    toast: ReturnType<typeof useToast>;
}) {
    const cancelButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const title =
        nextOption.value === true ? 'Mark Tow Vehicle as Held?' : 'Release Hold on Tow Vehicle?';

    return (
        <BlankModal isOpen={true} onClose={onClose} isCentered initialFocusRef={cancelButtonRef}>
            <ModalHeader style={{ border: 'none' }}>{title}</ModalHeader>
            <ModalBody />
            <ModalFooter style={{ border: 'none' }}>
                <ButtonGroup>
                    <Button
                        ref={cancelButtonRef}
                        variant="ghost"
                        onClick={onClose}
                        disabled={mutation.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="solid"
                        isLoading={mutation.isLoading}
                        onClick={() => {
                            if (!towVehicleId) {
                                return;
                            }
                            mutation
                                .mutateAsync({
                                    towVehicleId,
                                    hold: nextOption.value,
                                })
                                .then((resp) => {
                                    const isHeld = resp.data?.isHeld;
                                    const resolvedOption = options.find(
                                        (option) => option.value === Boolean(isHeld)
                                    );
                                    const finalOption = resolvedOption ?? nextOption;
                                    onConfirmChange(finalOption);
                                    onClose();
                                })
                                .catch(() => {
                                    toast({
                                        status: 'error',
                                        description: errorOccurredWhile(
                                            'updating tow vehicle hold'
                                        ),
                                    });
                                });
                        }}
                    >
                        Confirm
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </BlankModal>
    );
}
